import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Typography, InputAdornment, IconButton, Tooltip, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Send as SendIcon, FormatBold as BoldIcon, FormatItalic as ItalicIcon } from '@mui/icons-material';
import { sendPusherMessage } from '../services/pusherService';
import { usePusherChannel, usePusherConnection } from '../hooks/usePusher';
import { useAuth } from '../contexts/AuthContext';
import { useDebate } from '../contexts/DebateContext';
import { getDebateChannelName } from '../services/pusherService';
import { Trans, useTranslation } from 'react-i18next';
import { handlePlainTextPaste } from '../utils/messageFormatter';
import logger from '../utils/logger';
import { toast } from 'react-hot-toast';

const DRAFT_KEY_PREFIX = 'debate-draft-';

const getDraftKey = (debateId: string) => `${DRAFT_KEY_PREFIX}${debateId}`;

interface DebateMessageInputFormProps {
  debateId: string;
  teamId: string;
  isTeamTurn: boolean;
  isReformulating: boolean;
  reformulationMessage?: string;
  handleSendMessage?: (messageText: string) => Promise<void>;
}

const DebateMessageInputForm: React.FC<DebateMessageInputFormProps> = ({
  debateId,
  teamId,
  isTeamTurn,
  isReformulating,
  reformulationMessage,
  handleSendMessage: parentHandleSendMessage
}) => {
  const { t } = useTranslation();
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef('');
  messageInputRef.current = messageInput;
  const { sendMessage } = useDebate();
  const { user } = useAuth();
  const channelName = getDebateChannelName(debateId);
  usePusherChannel(channelName);
  const isPusherConnected = usePusherConnection();

  // Restore draft from localStorage on mount (survives refresh/network error)
  useEffect(() => {
    if (!debateId) return;
    // Don't restore when reformulating - reformulationMessage will be set by the other effect
    if (isReformulating && reformulationMessage) return;
    try {
      const stored = localStorage.getItem(getDraftKey(debateId));
      if (stored && stored.trim()) {
        const draft = stored.length <= 10000 ? stored : stored.substring(0, 10000);
        setMessageInput(draft);
        toast.success(t('messageInputDraftRestored', 'Draft restored. Your unsent message was saved.'));
      }
    } catch (e) {
      logger.warn('Failed to restore draft from localStorage', e);
    }
  }, [debateId, isReformulating, reformulationMessage, t]);

  // Debounced save of message input to localStorage
  useEffect(() => {
    if (!debateId) return;
    const key = getDraftKey(debateId);
    const timer = setTimeout(() => {
      try {
        if (messageInput.trim()) {
          localStorage.setItem(key, messageInput);
        } else {
          localStorage.removeItem(key);
        }
      } catch (e) {
        logger.warn('Failed to save draft to localStorage', e);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [debateId, messageInput]);

  // Focus the input field when it's the team's turn
  useEffect(() => {
    if (isTeamTurn && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isTeamTurn]);

  // Reset the message input when reformulation status changes
  useEffect(() => {
    if (isReformulating && reformulationMessage) {
      setMessageInput(reformulationMessage);
    }
  }, [isReformulating, reformulationMessage]);

  // Format selected text with bold or italic
  const formatSelectedText = (formatType: 'bold' | 'italic') => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const selectedText = messageInput.substring(start, end);

    if (selectedText.length === 0) {
      // No text selected, insert formatting markers at cursor
      const markers = formatType === 'bold' ? '****' : '**';
      const newValue = messageInput.substring(0, start) + markers + messageInput.substring(end);
      
      if (newValue.length <= 10000) {
        setMessageInput(newValue);
        // Position cursor between the markers
        setTimeout(() => {
          if (inputRef.current) {
            const cursorPos = start + (formatType === 'bold' ? 2 : 1);
            inputRef.current.setSelectionRange(cursorPos, cursorPos);
            inputRef.current.focus();
          }
        }, 0);
      }
    } else {
      // Text is selected, wrap it with formatting
      const wrapper = formatType === 'bold' ? '**' : '*';
      const formattedText = `${wrapper}${selectedText}${wrapper}`;
      const newValue = messageInput.substring(0, start) + formattedText + messageInput.substring(end);
      
      if (newValue.length <= 10000) {
        setMessageInput(newValue);
        // Select the formatted text (including markers)
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(start, start + formattedText.length);
            inputRef.current.focus();
          }
        }, 0);
      }
    }

    // Trigger typing notification
    if (isPusherConnected && !isTyping) {
      setIsTyping(true);
      sendTypingNotification(true).catch(() => {
        // Silently fail - typing is non-critical
      });
    }
  };

  // Paste plain text only (clipboard text/plain), like Notepad — predictable for users
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const convertedText = handlePlainTextPaste(e.nativeEvent as ClipboardEvent);
    
    if (convertedText !== null) {
      // Prevent default paste behavior
      e.preventDefault();
      
      // Get current cursor position
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      
      // Insert converted text at cursor position
      const currentValue = messageInput;
      const newValue = currentValue.substring(0, start) + convertedText + currentValue.substring(end);
      
      // Limit to 10000 characters
      if (newValue.length <= 10000) {
        setMessageInput(newValue);
        
        // Set cursor position after the pasted content
        setTimeout(() => {
          if (inputRef.current) {
            const newCursorPos = start + convertedText.length;
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
        }, 0);
        
        // Trigger typing notification
        if (isPusherConnected && !isTyping) {
          setIsTyping(true);
          sendTypingNotification(true).catch(() => {
            // Silently fail - typing is non-critical
          });
        }
      }
    }
  };

  // Handle input changes and typing notifications
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Limit to 10000 characters
    if (newValue.length <= 10000) {
      setMessageInput(newValue);
      
      // Only send typing notifications if we have a connection
      if (isPusherConnected) {
        // Handle typing notifications
        if (!isTyping) {
          setIsTyping(true);
          sendTypingNotification(true).catch(() => {
            // Silently fail - typing is non-critical
          });
        }
        
        // Reset typing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          sendTypingNotification(false).catch(() => {
            // Silently fail - typing is non-critical
          });
        }, 3000);
      }
    }
  };

  // Send typing notification through Pusher
  const sendTypingNotification = async (isTyping: boolean) => {
    if (!debateId || !teamId || !user?.id) return;
    
    try {
      await sendPusherMessage(
        channelName,
        'user_typing',
        {
          debateId,
          userId: user.id,
          teamId,
          username: user.username || 'A team member',
          isTyping
        }
      );
    } catch (error) {
      // Silently fail typing notifications - they're not critical
      logger.warn('Typing notification failed to send, but this is non-critical:', error);
      // Don't propagate error for typing notifications - they're not critical to functionality
    }
  };

  // Handle sending the message
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!messageInput.trim() || !isTeamTurn) return;

    // Persist draft immediately before send — optimistic UI may unmount us before debounce fires
    const textToSend = messageInput.trim();
    try {
      if (textToSend) {
        localStorage.setItem(getDraftKey(debateId), messageInput);
      }
    } catch {
      // Ignore
    }
    
    try {
      if (parentHandleSendMessage) {
        // Use parent handler if provided
        await parentHandleSendMessage(messageInput);
      } else {
        // Send the message using local implementation
        await sendMessage(
          debateId,
          messageInput.trim(),
          teamId,
          isReformulating
        );
      }

      // Clear draft from localStorage and input on success
      try {
        localStorage.removeItem(getDraftKey(debateId));
      } catch {
        // Ignore
      }
      setMessageInput('');
      
      // Stop typing notification
      setIsTyping(false);
      sendTypingNotification(false);
      
    } catch (error) {
      logger.error('Error sending message:', error);
      toast.error(t('messageInputSendFailedRestored', 'Send failed. Your message is still here — try again when connected.'));
    }
  };

  // Clean up typing timeout and persist draft on unmount (handles remount after network error)
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        sendTypingNotification(false);
      }
      // Persist draft on unmount — debounce may have been cancelled before firing
      try {
        const text = messageInputRef.current?.trim();
        if (debateId && text) {
          localStorage.setItem(getDraftKey(debateId), messageInputRef.current);
        }
      } catch {
        // Ignore
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup only on unmount
  }, [debateId]);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Formatting Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        mb: 1, 
        alignItems: 'center',
        opacity: (!isTeamTurn || !debateId) ? 0.5 : 1 
      }}>
        <Tooltip title={t('boldTooltip', 'Bold (**text**)')}>
          <IconButton
            size="small"
            onClick={() => formatSelectedText('bold')}
            disabled={!isTeamTurn || !debateId}
            sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <BoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={t('italicTooltip', 'Italic (*text*)')}>
          <IconButton
            size="small"
            onClick={() => formatSelectedText('italic')}
            disabled={!isTeamTurn || !debateId}
            sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        

      </Box>

      {/* Message Input Form */}
      <Box component="form" onSubmit={handleSendMessage}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={isReformulating ? t('reformulateYourMessagePlaceholder') : t('typeYourMessagePlaceholder')}
          value={messageInput}
          onChange={handleInputChange}
          onPaste={handlePaste}
          disabled={!isTeamTurn || !debateId}
          inputRef={inputRef}
          multiline
          minRows={2}
          maxRows={6}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  type="submit"
                  variant="contained"
                  color={isPusherConnected ? "primary" : "warning"}
                  endIcon={<SendIcon />}
                  disabled={!messageInput.trim() || !isTeamTurn}
                  sx={{ ml: 1 }}
                >
                  {isReformulating ? t('reformulateButton') : isPusherConnected ? t('messageInputSendButton') : t('messageInputSendButtonOffline')}
                </Button>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {messageInput.length}/10000 {t('messageInputCharactersText')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          {t('formattingHint', 'Use **bold** and *italic* in plain text, or the toolbar. Paste uses plain text only (like Notepad).')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          <Trans
            i18nKey="linkGuidanceWithLink"
            components={[
              <Link key="whitelist-link" component={RouterLink} to="/link-whitelist" underline="hover" />
            ]}
            defaultValue={'Links are for reference only. Summarize your point; don\'t offload reading. ≤2 links. Off-list URLs show as text with a short notice. See <0>Whitelisted websites</0>.'}
          />
        </Typography>
        {!isPusherConnected && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            {t('messageInputConnectionIssuesWarning')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DebateMessageInputForm; 